/**
 * DataTable Component
 * 
 * Table component for displaying office employee salary and bonus data.
 * Displays data in a formatted table with columns for:
 * - Year, Month/Period
 * - Employee code, name, department
 * - Payment amounts (基本給/賞与, 支給合計/他手当計, 控除合計)
 * - Taxable amount (課税対象額)
 * - Actual payment (実支給額)
 * - Delete operation per row
 * 
 * Features:
 * - Supports both salary and bonus data types
 * - Currency formatting with Japanese locale
 * - Period/month display formatting for bonus data
 * - Individual record deletion with confirmation
 * - Empty state handling
 * - Loading state for delete operations
 * 
 * @component
 */

import React from 'react';
import { SalaryData, BonusData, DataType } from './types/DataDisplayTypes';

/**
 * Props interface for DataTable component.
 */
interface DataTableProps {
  dataType: DataType;                                                           // Current data type ('salary' or 'bonus')
  filteredData: (SalaryData | BonusData)[];                                     // Array of data records to display
  deleteLoading: boolean;                                                        // Loading state for delete operations
  onDeleteIndividualRecord: (employeeCode: string, year: number, month: number, employeeName: string, period?: string) => void; // Delete handler for individual records
}

/**
 * DataTable Component
 * 
 * Renders a table displaying salary or bonus data records.
 * 
 * @param props - Component props
 * @returns React functional component
 */
export const DataTable: React.FC<DataTableProps> = ({
  dataType,
  filteredData,
  deleteLoading,
  onDeleteIndividualRecord
}) => {
  /**
   * Formats a number as Japanese currency format.
   * 
   * Uses Intl.NumberFormat with 'ja-JP' locale to format numbers
   * with appropriate thousands separators.
   * 
   * @param amount - Number to format
   * @returns Formatted string (e.g., "1,234,567")
   * 
   * @example
   * ```typescript
   * formatCurrency(1234567); // Returns: "1,234,567"
   * ```
   */
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP').format(amount);
  };

  /**
   * Empty state - shows message when no data is available.
   */
  if (filteredData.length === 0) {
    return (
      <div className="data-table-container">
        <div className="no-data">
          <p>表示するデータがありません</p>
        </div>
      </div>
    );
  }

  return (
    <div className="data-table-container">
      <table className="data-table">
        {/* Table Header */}
        <thead>
          <tr>
            <th>年</th>
            <th>{dataType === 'salary' ? '月' : '月'}</th> {/* Month column (same label for both types) */}
            <th>従業員コード</th>
            <th>氏名</th>
            <th>所属</th>
            {/* Payment column: 基本給 for salary, 賞与 for bonus */}
            <th>{dataType === 'salary' ? '基本給' : '賞与'}</th>
            {/* Total column: 支給合計 for salary, 他手当計 for bonus */}
            <th>{dataType === 'salary' ? '支給合計' : '他手当計'}</th>
            <th>控除合計</th> {/* Same for both types */}
            <th>課税対象額</th>
            <th>実支給額</th>
            <th>操作</th> {/* Actions column */}
          </tr>
        </thead>
        <tbody>
          {/* Map through filtered data and render rows */}
          {filteredData.map((data, index) => {
            /**
             * Salary data row rendering.
             * Displays salary-specific fields and values.
             */
            if (dataType === 'salary') {
              const salaryData = data as SalaryData;
              return (
                <tr key={`${salaryData.employeeCode}-${salaryData.year}-${salaryData.month}-${index}`}>
                  <td>{salaryData.year}</td>
                  <td>{salaryData.month}</td>
                  <td>{salaryData.employeeCode}</td>
                  <td>{salaryData.employeeName}</td>
                  <td>{salaryData.department}</td>
                  {/* Currency-formatted amounts with fallback to 0 */}
                  <td>¥{formatCurrency(salaryData.baseSalary || 0)}</td>
                  <td>¥{formatCurrency(salaryData.totalPayment || 0)}</td>
                  <td>¥{formatCurrency(salaryData.totalDeductions || 0)}</td>
                  <td>¥{formatCurrency(salaryData.taxableAmount || 0)}</td>
                  <td className="net-salary">¥{formatCurrency(salaryData.actualPayment || 0)}</td>
                  <td>
                    {/* Delete button with confirmation dialog */}
                    <button
                      className="delete-row-btn"
                      onClick={() => { 
                        if (window.confirm('本当に削除してもよろしいですか？')) { 
                          onDeleteIndividualRecord(
                            salaryData.employeeCode, 
                            salaryData.year, 
                            salaryData.month,
                            salaryData.employeeName
                          ); 
                        } 
                      }}
                      disabled={deleteLoading}
                      title="このレコードを削除"
                    >
                      {deleteLoading ? '削除中...' : '削除'}
                    </button>
                  </td>
                </tr>
              );
            } else {
              /**
               * Bonus data row rendering.
               * Displays bonus-specific fields and values.
               */
              const bonusData = data as BonusData;
              
              /**
               * Displays the bonus period correctly.
               * 
               * Converts period values (numbers, English season names, Japanese) to
               * display format. Handles:
               * - Month numbers (1-12) -> "X月"
               * - English season names (summer, winter, etc.) -> Japanese month equivalents
               * - Other period strings -> as-is
               * 
               * @param period - Period value (string, number, or null)
               * @returns Formatted period string for display
               */
              const displayBonusPeriod = (period: string | number | null) => {
                if (!period) return '未設定';
                
                /**
                 * If period is a number (month 1-12), format it as "X月".
                 * For example: 7 -> "7月"
                 */
                const monthNum = parseInt(period.toString());
                if (!isNaN(monthNum) && monthNum >= 1 && monthNum <= 12) {
                  return `${monthNum}月`;
                }
                
                /**
                 * Fallback for legacy season-based periods.
                 * Converts English season names to Japanese month equivalents.
                 */
                const periodStr = period.toString().toLowerCase();
                switch (periodStr) {
                  case 'summer':
                    return '8月';
                  case 'winter':
                    return '12月';
                  case 'spring':
                    return '3月';
                  case 'autumn':
                    return '11月';
                  case 'annual':
                    return '年末';
                  default:
                    return period.toString();
                }
              };
              
              /**
               * Extracts month from bonus name or falls back to period display.
               * 
               * Attempts to extract month from bonus name patterns like "R6年7月賞与" -> "7月".
               * If no month pattern is found, falls back to displaying the period value.
               * 
               * @param bonusName - Bonus name string (may contain month pattern)
               * @returns Month string in "X月" format, or bonus name/period if no match
               * 
               * @example
               * ```typescript
               * extractMonthFromBonusName('R6年7月賞与'); // Returns: "7月"
               * extractMonthFromBonusName(null); // Returns: displayBonusPeriod(bonusData.bonusPeriod)
               * ```
               */
              const extractMonthFromBonusName = (bonusName: string | null | undefined): string => {
                if (!bonusName) return displayBonusPeriod(bonusData.bonusPeriod);
                
                /**
                 * Extract month from patterns like "R6年7月賞与" -> "7月".
                 * Uses regex to find digit followed by 月 character.
                 */
                const monthMatch = bonusName.match(/(\d+)月/);
                if (monthMatch) {
                  return `${monthMatch[1]}月`;
                }
                
                // Fallback to original bonus name if no month pattern found
                return bonusName;
              };
              
              /**
               * Get display month from bonus name or period.
               * Extracts month from bonus name if available, otherwise uses period.
               */
              const displayMonth = extractMonthFromBonusName(bonusData.bonusName);
              
              return (
                <tr key={`${bonusData.employeeCode}-${bonusData.year}-${bonusData.bonusPeriod}-${index}`}>
                  <td>{bonusData.year}</td>
                  <td>{displayMonth}</td>
                  <td>{bonusData.employeeCode}</td>
                  <td>{bonusData.employeeName}</td>
                  <td>{bonusData.department}</td>
                  {/* Currency-formatted amounts with fallback to 0 */}
                  <td>¥{formatCurrency(bonusData.bonus || 0)}</td>
                  <td>¥{formatCurrency(bonusData.otherAllowancesTotal || 0)}</td>
                  <td>¥{formatCurrency(bonusData.totalDeductions || 0)}</td>
                  <td>¥{formatCurrency(bonusData.taxableAmount || 0)}</td>
                  <td className="net-salary">¥{formatCurrency(bonusData.actualPaymentAmount || 0)}</td>
                  <td>
                    {/* Delete button with confirmation dialog */}
                    <button
                      className="delete-row-btn"
                      onClick={() => { 
                        if (window.confirm('本当に削除してもよろしいですか？')) { 
                          onDeleteIndividualRecord(
                            bonusData.employeeCode, 
                            bonusData.year,
                            0, // No month for bonus data (uses period instead)
                            bonusData.employeeName,
                            bonusData.bonusPeriod
                          ); 
                        } 
                      }}
                      disabled={deleteLoading}
                      title="このレコードを削除"
                    >
                      {deleteLoading ? '削除中...' : '削除'}
                    </button>
                  </td>
                </tr>
              );
            }
          })}
        </tbody>
      </table>
    </div>
  );
}; 